const ENGINEERING_HOD_NAMES = new Set([
  'Programming HOD',
  'Electronic HOD',
  'Mechanism HOD',
]);

export function isTeamLeader(user) {
  if (!user) return false;
  return Boolean(user.is_team_leader) || Number(user.management_id) === 1;
}

export function isEngineeringHod(user) {
  const name = user?.management?.name;
  return name != null && ENGINEERING_HOD_NAMES.has(name);
}

/** Department the user leads when they are an engineering HOD (matches backend users.department_id). */
export function engineeringHodDepartmentId(user) {
  return isEngineeringHod(user) && user?.department_id != null
    ? Number(user.department_id)
    : null;
}

export function canCreateTask(user) {
  if (!user) return false;
  if (isTeamLeader(user)) return true;
  return isEngineeringHod(user);
}

export function taskTouchesDepartment(task, departmentId) {
  if (departmentId == null || !task?.departments?.length) return false;
  return task.departments.some((d) => Number(d.id) === Number(departmentId));
}

/** Mirrors backend: TL see all; everyone else sees tasks linked to their department. */
export function canViewTask(user, task) {
  if (!user || !task) return false;
  if (isTeamLeader(user)) return true;
  if (user.department_id == null) return false;
  return taskTouchesDepartment(task, user.department_id);
}

/** Full edit: TL any task; engineering HOD only tasks in their department. */
export function canFullEditTask(user, task) {
  if (!user || !task) return false;
  if (isTeamLeader(user)) return true;
  const hodDept = engineeringHodDepartmentId(user);
  if (hodDept != null && taskTouchesDepartment(task, hodDept)) return true;
  return false;
}

export function canArchiveTask(user, task) {
  return canFullEditTask(user, task) && !task?.archived_at;
}

export function canRestoreTask(user, task) {
  return canFullEditTask(user, task) && Boolean(task?.archived_at);
}

export function isTaskAssignee(user, task) {
  if (!user || !task) return false;
  return task.users?.some((u) => Number(u.id) === Number(user.id)) ?? false;
}

/** Limited form edit for assignees: description, links, milestones, status — not title or assignments. */
export function canAssigneeEditTask(user, task) {
  if (!user || !task) return false;
  if (canFullEditTask(user, task)) return false;
  return isTaskAssignee(user, task);
}

/** Open edit form or save task changes (full or assignee-limited). */
export function canEditTask(user, task) {
  if (!user || !task) return false;
  return canFullEditTask(user, task) || canAssigneeEditTask(user, task);
}

/**
 * Milestone checklist (toggle done): team leader / relevant HOD, or a user assigned to the task.
 * Assignees may also use the edit form for description, links, milestones list, and status.
 */
export function canEditTaskMilestones(user, task) {
  if (!user || !task) return false;
  if (canFullEditTask(user, task)) return true;
  return isTaskAssignee(user, task);
}

/** Kanban drag / status changes: same as milestone toggles. */
export function canUpdateTaskStatus(user, task) {
  return canEditTaskMilestones(user, task);
}

/** HOD-only limited scope (not admin/TL). */
export function isEngineeringHodScoped(user) {
  if (!user) return false;
  return engineeringHodDepartmentId(user) != null && isEngineeringHod(user) && !isTeamLeader(user);
}
